import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { CustomHeroButton, Typography } from '../components';
import { Link } from 'react-router';
import { useTranslations } from '../contexts/translation.context';
import { StatCard } from './components/StatCard';
import { SimpleStatCard } from './components/SimpleStatCard';
import { StatSection } from './components/StatSection';
import { NewsBlock } from './components/NewsBlock';
import { useAccount } from '../contexts/AccountContext';
import UserIc from './user.svg';
import { clientAuthService } from '../api';
import { useIsMobile } from '../hooks/useMobile';

type DashboardResponse = {
  models?: {
    active?: number;
    moderation?: number;
    deactivated?: number;
  };
  subscribers?: {
    today?: number;
    yesterday?: number;
    last7days?: number;
  };
  transactions?: {
    today?: string | number;
    yesterday?: string | number;
    last7days?: string | number;
  };
  invoices?: {
    paid?: number;
    unpaid?: number;
    cancel?: number;
  };
};

const numberFormatter = new Intl.NumberFormat('ru-RU');
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatSignedNumber = (value: number) => {
  const formatted = numberFormatter.format(Math.abs(value));
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return '0';
};

const formatSignedCurrency = (value: number) => {
  const formatted = currencyFormatter.format(Math.abs(value));
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return currencyFormatter.format(0);
};

const calculatePercentChange = (current: number, previous: number) => {
  if (!previous) return '0%';
  const diff = ((current - previous) / Math.abs(previous)) * 100;
  const fixed = diff.toFixed(1);
  return `${diff >= 0 ? '+' : ''}${fixed}%`;
};

const toNumber = (value?: string | number | null) => {
  if (value === null || value === undefined) return 0;
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(parsed) ? parsed : 0;
};

export default function Dashboard() {
  const isMobile = useIsMobile();
  const { accounts } = useAccount();
  const { t } = useTranslations();
  const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);

  const getData = useCallback(async () => {
    try {
      const res = await clientAuthService.getDashboard();
      if (res?.data) {
        setDashboardData(res.data);
      }
    } catch (err: any) {
      console.log('errgetDashboard', err);
    }
  }, []);

  useEffect(() => {
    getData();
    const interval = setInterval(() => {
      getData();
    }, 10000);
    return () => clearInterval(interval);
  }, [getData]);

  const dateLabels = useMemo(() => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - 6);

    const formatDate = (date: Date) => date.toLocaleDateString('ru-RU');

    return {
      today: formatDate(now),
      yesterday: formatDate(yesterday),
      week: `${formatDate(weekStart)} - ${formatDate(now)}`,
    };
  }, [dashboardData]);

  const accountsStats = useMemo(
    () => ({
      active: dashboardData?.models?.active ?? accounts?.length ?? 0,
      moderation: dashboardData?.models?.moderation ?? 0,
      disabled: dashboardData?.models?.deactivated ?? 0,
    }),
    [dashboardData, accounts]
  );

  const paymentsStats = useMemo(
    () => ({
      waiting: dashboardData?.invoices?.unpaid ?? 0,
      paid: dashboardData?.invoices?.paid ?? 0,
      rejected: dashboardData?.invoices?.cancel ?? 0,
    }),
    [dashboardData]
  );

  const subscribersStats = useMemo(() => {
    const todayValue = dashboardData?.subscribers?.today ?? 0;
    const yesterdayValue = dashboardData?.subscribers?.yesterday ?? 0;
    const weekValue = dashboardData?.subscribers?.last7days ?? 0;

    return {
      today: {
        value: formatSignedNumber(todayValue),
        date: dateLabels.today,
        percent: calculatePercentChange(todayValue, yesterdayValue),
        isPositive: todayValue >= yesterdayValue,
      },
      yesterday: {
        value: formatSignedNumber(yesterdayValue),
        date: dateLabels.yesterday,
        percent: calculatePercentChange(yesterdayValue, todayValue),
        isPositive: yesterdayValue >= todayValue,
      },
      week: {
        value: formatSignedNumber(weekValue),
        date: dateLabels.week,
        percent: calculatePercentChange(weekValue, yesterdayValue ? yesterdayValue * 7 : weekValue),
        isPositive: weekValue >= (yesterdayValue ? yesterdayValue * 7 : 0),
      },
    };
  }, [dashboardData, dateLabels]);

  const incomeStats = useMemo(() => {
    const todayValue = toNumber(dashboardData?.transactions?.today);
    const yesterdayValue = toNumber(dashboardData?.transactions?.yesterday);
    const weekValue = toNumber(dashboardData?.transactions?.last7days);

    return {
      today: {
        value: formatSignedCurrency(todayValue),
        date: dateLabels.today,
        percent: calculatePercentChange(todayValue, yesterdayValue),
        isPositive: todayValue >= yesterdayValue,
      },
      yesterday: {
        value: formatSignedCurrency(yesterdayValue),
        date: dateLabels.yesterday,
        percent: calculatePercentChange(yesterdayValue, todayValue),
        isPositive: yesterdayValue >= todayValue,
      },
      week: {
        value: formatSignedCurrency(weekValue),
        date: dateLabels.week,
        percent: calculatePercentChange(weekValue, yesterdayValue ? yesterdayValue * 7 : weekValue),
        isPositive: weekValue >= (yesterdayValue ? yesterdayValue * 7 : 0),
      },
    };
  }, [dashboardData, dateLabels]);

  return (
    <div className="flex flex-col gap-6 md:gap-4 p-4 py-6 md:px-6 md:py-4">
      {/* Первый блок - Аккаунты OnlyFans и Оплаты */}
      <Typography variant="h5">{t('dashboard.title')}</Typography>

      {accounts?.length === 0 || !accounts ? (
        <div className="flex flex-col text-center items-center justify-center gap-4 py-2 md:p-16">
          <div className="icon size-12 md:size-[6.25rem]">
            <img src={UserIc} alt="No accounts" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Typography variant="h6" className="max-md:font-semibold">
              {t('dashboard.noAccounts')}
            </Typography>
            <Typography className="text-neutral-500" variant="body1">
              {t('dashboard.followSteps')}
            </Typography>
          </div>
          <Link to="/client/accounts/create">
            <CustomHeroButton
              title={t('dashboard.addAccount')}
              variant="primary"
              className="h-12 px-5 !text-base !leading-6 !font-semibold py-3 max-md:mt-2"
              size="md"
            />
          </Link>
        </div>
      ) : (
        <>
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <StatSection
                title={t('dashboard.accountsOnlyFans')}
                linkText={t('dashboard.allAccounts')}
                linkTo="/client/accounts"
              >
                <SimpleStatCard label={t('labels.activeAccess')} value={accountsStats.active} />
                <SimpleStatCard label={t('labels.onModeration')} value={accountsStats.moderation} />
                <SimpleStatCard label={t('labels.disabled')} value={accountsStats.disabled} />
              </StatSection>

              <StatSection
                title={t('dashboard.payments')}
                linkText={t('dashboard.allBills')}
                linkTo="/client/account/wallet"
              >
                <SimpleStatCard label={t('labels.waitingPayment')} value={paymentsStats.waiting} />
                <SimpleStatCard label={t('labels.paid')} value={paymentsStats.paid} />
                <SimpleStatCard label={t('labels.rejected')} value={paymentsStats.rejected} />
              </StatSection>

              {/* Новости от OnlyU */}
              {!isMobile && (
                <div className="md:mt-9 md:mb-2">
                  <NewsBlock />
                </div>
              )}
            </div>
          </div>

          {/* Подписчики и Доход */}
          <div className="mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <StatSection
                title={t('dashboard.subscribers')}
                linkText={t('dashboard.allSubscribers')}
                linkTo="/client/statistics/subscribers"
              >
                <StatCard
                  title={t('dashboard.today')}
                  date={subscribersStats.today.date}
                  value={subscribersStats.today.value}
                  percent={subscribersStats.today.percent}
                  isPositive={subscribersStats.today.isPositive}
                />
                <StatCard
                  title={t('dashboard.yesterday')}
                  date={subscribersStats.yesterday.date}
                  value={subscribersStats.yesterday.value}
                  percent={subscribersStats.yesterday.percent}
                  isPositive={subscribersStats.yesterday.isPositive}
                />
                <StatCard
                  title={t('dashboard.average7Days')}
                  date={subscribersStats.week.date}
                  value={subscribersStats.week.value}
                  percent={subscribersStats.week.percent}
                  isPositive={subscribersStats.week.isPositive}
                />
              </StatSection>

              <StatSection
                title={t('dashboard.income')}
                linkText={t('dashboard.allTransactions')}
                linkTo="/client/statistics/transactions"
              >
                <StatCard
                  title={t('dashboard.today')}
                  date={incomeStats.today.date}
                  value={incomeStats.today.value}
                  percent={incomeStats.today.percent}
                  isPositive={incomeStats.today.isPositive}
                />
                <StatCard
                  title={t('dashboard.yesterday')}
                  date={incomeStats.yesterday.date}
                  value={incomeStats.yesterday.value}
                  percent={incomeStats.yesterday.percent}
                  isPositive={incomeStats.yesterday.isPositive}
                />
                <StatCard
                  title={t('dashboard.average7Days')}
                  date={incomeStats.week.date}
                  value={incomeStats.week.value}
                  percent={incomeStats.week.percent}
                  isPositive={incomeStats.week.isPositive}
                />
              </StatSection>
            </div>
          </div>

          {isMobile && (
            <div className="mt-2">
              <NewsBlock />
            </div>
          )}
        </>
      )}
    </div>
  );
}
